import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AttendanceStatus, SessionAttendance as Data } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { Alert, Button } from '../ui';
import { AttendanceList, attendanceSummary } from './AttendanceList';

export function useAttendance(teamId: string, sessionId: string) {
  return useQuery({ queryKey: ['attendance', sessionId], queryFn: () => api.get<Data>(`/teams/${teamId}/sessions/${sessionId}/attendance`) });
}

/** Attendance on the session page: who's expected, who can't make it, and marking who came. */
export function SessionAttendance({ teamId, sessionId }: { teamId: string; sessionId: string }) {
  const qc = useQueryClient();
  const q = useAttendance(teamId, sessionId);
  const [open, setOpen] = useState(false);
  const mark = useMutation({
    mutationFn: (m: { playerId: string; status: AttendanceStatus | null }) => api.put<Data>(`/teams/${teamId}/sessions/${sessionId}/attendance`, { marks: [m] }),
    onMutate: (m) => {
      // Show the tap straight away.
      qc.setQueryData<Data>(['attendance', sessionId], (d) => d && { ...d, rows: d.rows.map((r) => (r.player.id === m.playerId ? { ...r, status: m.status } : r)) });
    },
    onSuccess: (d) => qc.setQueryData(['attendance', sessionId], d),
    onError: () => qc.invalidateQueries({ queryKey: ['attendance', sessionId] }),
  });

  if (q.isLoading || !q.data) return null;
  const d = q.data;
  const unavailable = d.rows.filter((r) => r.unavailable);

  return (
    <section className="mb-8 rounded-lg border border-line bg-surface">
      <button className="flex w-full items-center gap-3 px-4 py-3 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? <ChevronDown className="size-4 text-muted" aria-hidden /> : <ChevronRight className="size-4 text-muted" aria-hidden />}
        <span className="font-semibold">Attendance</span>
        <span className="tnum min-w-0 flex-1 truncate text-[14px] text-muted">
          {!d.rows.length
            ? 'Add players on the team’s Roster tab to take attendance.'
            : d.taken
              ? attendanceSummary(d.rows)
              : `${d.expected} of ${d.rows.length} expected${unavailable.length ? `; ${unavailable.length} can’t make it` : ''}`}
        </span>
      </button>
      {open && (
        <div className="border-t border-line px-4 py-3">
          {mark.isError && <div className="mb-3"><Alert>{errorMessage(mark.error)}</Alert></div>}
          {!d.rows.length ? (
            <Link to={`/teams/${teamId}?tab=roster`} className="btn-secondary">
              Go to the roster
            </Link>
          ) : (
            <>
              <AttendanceList rows={d.rows} readOnly={!d.canMark} onSet={(playerId, status) => mark.mutate({ playerId, status })} />
              {d.canMark && !d.taken && (
                <Button
                  variant="secondary"
                  className="btn-secondary mt-3"
                  onClick={() =>
                    api
                      .put<Data>(`/teams/${teamId}/sessions/${sessionId}/attendance`, {
                        marks: d.rows.filter((r) => !r.status).map((r) => ({ playerId: r.player.id, status: r.unavailable ? 'absent' : 'present' })),
                      })
                      .then((x) => qc.setQueryData(['attendance', sessionId], x))
                  }
                >
                  Mark everyone else here
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
